import React, { useState, useEffect, useCallback, useRef } from "react";
import { ENABLE_GIFT_CARDS } from "../../constants/featureFlags";
import { useNavigate } from "react-router-dom";
import {
  executeQuery,
  executeMutation,
  createOrder,
} from "../../graphql";
import { getVoucherBalance } from "../../graphql/queries";
import Wizard from "../../components/Wizard";
import Tooltip from "../../components/Tooltip";
import { usePageContent } from "../../hooks/usePageContent";
import {
  NEW_ORDER_TYPE,
  VPLATES_CHANNEL_ID,
  ORDER_NUMER_FORMAT,
  ORDER_NUMER_TYPE
} from "../../constants/stringVariables";
import {
  PRODUCT_TYPE,
  plateItemFromOrder,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPayment,
} from "../../utils/analytics";
import styles from "./index.module.css";

const CreatePaymentPage = () => {
  const navigate = useNavigate();
  const { find } = usePageContent('create-payment');
  const wizardContent = find('wizard')       || {};
  const c             = find('paymentForm')  || {};
  const ce            = c.errors             || {};
  const publishableApiKey    = import.meta.env.VITE_QUICKSTREAM_PUBLISHABLE_KEY;
  const supplierBusinessCode = import.meta.env.VITE_QUICKSTREAM_SUPPLIER_CODE;

  const session = (() => { try { return JSON.parse(sessionStorage.getItem('plateOrderData') || '{}'); } catch { return {}; } })();
  const combination = session.combination || '';
  const vehicleType = session.vehicleType || 'car';

  const wizardSteps = wizardContent.steps || [
    { title: 'Check combination', href: `/create/check-combination` },
    { title: 'Select a style', href: '/create/select-a-style' },
    { title: 'Confirm personal details', href: '/create/personal-details' },
    { title: 'Checkout' },
  ];

  const [trustedFrame,      setTrustedFrame]      = useState(null);
  const [orderData,         setOrderData]          = useState(null);
  const [loading,           setLoading]            = useState(true);
  const [paymentProcessing, setPaymentProcessing]  = useState(false);
  const [error,             setError]              = useState(null);
  const [attemptsMade,      setAttemptsMade]       = useState(0);
  const [isSubmittingOrder, setIsSubmittingOrder]  = useState(false);
  const [surchargePercentage, setSurchargePercentage] = useState(parseFloat(import.meta.env.VITE_QUICKSTREAM_SURCHARGE_PERCENTAGE) || 0);
  const [timerExpired, setTimerExpired] = useState(false);
  const [timerOutput,  setTimerOutput]  = useState('');
  const maxAttempts = 3;

  const [voucherCode,     setVoucherCode]     = useState("");
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [voucherError,    setVoucherError]    = useState("");
  const [appliedVoucher,  setAppliedVoucher]  = useState(null);
  const [termsAccepted,   setTermsAccepted]   = useState(false);

  const basePrice          = parseFloat(orderData?.price) || 0;
  const voucherDiscount    = appliedVoucher ? Math.min(appliedVoucher.balance, basePrice) : 0;
  const cardAmount         = Math.max(0, basePrice - voucherDiscount);
  const voucherFullyCovered = appliedVoucher !== null && cardAmount === 0;
  const cardSurcharge      = (cardAmount * surchargePercentage) / 100;

  useEffect(() => {
    const storedOrderData = sessionStorage.getItem("plateOrderData");
    if (!storedOrderData) {
      navigate('/create/select-a-style');
    } else {
      setOrderData(JSON.parse(storedOrderData));
      setLoading(false);
    }
  }, [navigate]);

  // GA: add_shipping_info on checkout load (prod: confirmOrder.js). Once per mount (StrictMode-safe).
  const shippingTrackedRef = useRef(false);
  useEffect(() => {
    if (!orderData?.variantId || shippingTrackedRef.current) return;
    shippingTrackedRef.current = true;
    trackAddShippingInfo(plateItemFromOrder(orderData, PRODUCT_TYPE.CREATE), orderData.price);
  }, [orderData]);

  const trackPlaceOrderClick = () => {
    if (orderData) trackAddPaymentInfo(plateItemFromOrder(orderData, PRODUCT_TYPE.CREATE), orderData.price);
  };

  const trackOrderPlaced = (orderNumber) => {
    if (orderData) trackPayment(plateItemFromOrder(orderData, PRODUCT_TYPE.CREATE), orderData.price, orderNumber);
  };

  const initializeQuickStream = useCallback(() => {
    if (!window.QuickstreamAPI) {
      setError("Payment system is not available. Please refresh the page.");
      return;
    }
    try {
      window.QuickstreamAPI.init({ publishableApiKey });

      const inputStyle = {
        height: "auto", border: "0", background: "none", "border-radius": "0",
        color: "#333", padding: "14px 0 10px", "line-height": "1",
        "font-size": "16px", "border-bottom": "2px solid #d2d1d2",
      };

      const options = {
        config: { supplierBusinessCode, surchargePercentage },
        iframe: { width: "100%", height: window.innerWidth < 1440 ? "450px" : "430px" },
        body: { style: { background: "none" } },
        labels: { style: { color: "#231F20", "font-weight": "700", "line-height": "1.5", "font-size": "16px" } },
        cardholderName: { label: "Card holder name",       placeholder: "E.g. Graham Smith",              style: inputStyle },
        cardNumber:     { label: "Credit card number",     placeholder: "E.g. 1234 1234 1234 1234",       style: inputStyle },
        expiryDateMonth:{ label: "Expiry date",                                                            style: inputStyle },
        expiryDateYear: { label: "Expiry year",                                                            style: inputStyle },
        cvn:            { label: "CVV number",             placeholder: "E.g. 388",
                          style: { ...inputStyle, width: "84px" } },
      };

      window.QuickstreamAPI.creditCards.createTrustedFrame(options, (errors, data) => {
        if (errors) {
          const msg = Array.isArray(errors)
            ? errors.map((e) => e.message || e).join(". ")
            : "Failed to initialize payment system";
          setError(msg);
        } else {
          setTrustedFrame(data.trustedFrame);
        }
      });
    } catch (err) {
      setError("Failed to initialize payment system. Please refresh the page.");
    }
  }, []);

  useEffect(() => {
    if (!orderData) return;
    const existingScript = document.getElementById("quickstream-script");
    if (existingScript) {
      if (window.QuickstreamAPI) initializeQuickStream();
      return;
    }
    const script = document.createElement("script");
    script.id    = "quickstream-script";
    script.src   = `${import.meta.env.VITE_QUICKSTREAM_BASE_URL}quickstream-api-1.0.min.js`;
    script.async = true;
    script.onload  = () => initializeQuickStream();
    script.onerror = () => setError("Failed to load payment system. Please refresh the page.");
    document.body.appendChild(script);
    return () => {
      const el = document.getElementById("quickstream-script");
      if (el) document.body.removeChild(el);
    };
  }, [orderData, initializeQuickStream]);

  useEffect(() => {
    if (!orderData) return;

    const lockMinutes = parseInt(import.meta.env.VITE_AWS_PLATE_LOCKTIME, 10) || 15;
    const maxDiffSecs = lockMinutes * 60;
    const nowSecs = () => Math.round(Date.now() / 1000);

    let expiry = parseInt(sessionStorage.getItem('plateLockExpiry'), 10);
    if (!expiry || expiry <= nowSecs()) {
      expiry = nowSecs() + maxDiffSecs;
      sessionStorage.setItem('plateLockExpiry', String(expiry));
    }

    const tick = () => {
      const now = nowSecs();
      let diff = expiry - now;

      if (diff > maxDiffSecs) {
        expiry = now + maxDiffSecs;
        sessionStorage.setItem('plateLockExpiry', String(expiry));
        diff = maxDiffSecs;
      }

      if (diff <= 0) {
        setTimerExpired(true);
        setTimerOutput('00:00');
        clearInterval(intervalId);
        return;
      }

      const mins = Math.floor(diff / 60);
      const secs = diff % 60;
      setTimerOutput(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [orderData]);

  const handleApplyVoucher = async () => {
    const trimmed = voucherCode.trim();
    if (!trimmed) { setVoucherError(ce.voucherEmpty || "Please enter a gift voucher code."); return; }
    setVoucherChecking(true);
    setVoucherError("");
    try {
      const data   = await executeQuery(getVoucherBalance, { code: trimmed });
      const result = data?.getVoucherBalance;
      if (!result) { setVoucherError(ce.voucherNotFound || "Gift voucher not found. Please check the code and try again."); return; }
      if (result.balance <= 0) { setVoucherError(ce.voucherNoBalance || "This gift voucher has no remaining balance."); return; }
      setAppliedVoucher({ code: trimmed, balance: result.balance, currency: result.currency || "AUD" });
    } catch (err) {
      const isInvalidCode = Array.isArray(err) && err.some((e) => {
        try { const info = JSON.parse(e?.errorInfo ?? "{}"); return "InvalidCode" in (info?.details ?? {}); }
        catch { return false; }
      });
      setVoucherError(isInvalidCode
        ? (ce.voucherInvalidCode  || "No gift voucher exists with that number. Please check and try again.")
        : (ce.voucherVerifyFailed || "Unable to verify gift voucher. Please try again later.")
      );
    } finally {
      setVoucherChecking(false);
    }
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucher(null);
    setVoucherCode("");
    setVoucherError("");
  };

  // Single-step: verify card token then immediately place order
  const handlePlaceOrder = () => {
    if (!trustedFrame) {
      setError("Payment system not initialized. Please refresh the page.");
      return;
    }
    // GA: add_payment_info on PLACE ORDER click, before card verification (prod: orderPayment.js).
    trackPlaceOrderClick();
    setPaymentProcessing(true);
    setError(null);

    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      setPaymentProcessing(false);
      setError(ce.cardVerifyTimeout || "Card verification timed out. Please try again.");
    }, 30000);

    trustedFrame.submitForm(async (errors, data) => {
      if (timedOut) return;
      clearTimeout(timeoutId);

      if (errors) {
        setPaymentProcessing(false);
        const fieldLabels = {
          cardholderName: "Card holder name",
          cardNumber: "Credit card number",
          expiryDateMonth: "Expiry date",
          expiryDateYear: "Expiry year",
          cvn: "CVV number",
        };
        const isValidationError = Array.isArray(errors) && errors.some((e) => e?.fieldName);
        if (isValidationError) {
          const messages = errors.flatMap((e) => {
            const label = fieldLabels[e?.fieldName] || "";
            if (Array.isArray(e?.messages)) return e.messages.map((m) => ({ label, message: m }));
            if (typeof e?.message === "string") return [{ label, message: e.message }];
            if (typeof e === "string") return [{ label: "", message: e }];
            return [{ label: "", message: ce.cardVerifyFailed || "Card verification failed." }];
          });
          setError(messages);
        } else {
          setAttemptsMade((prev) => {
            const next = prev + 1;
            if (next >= maxAttempts) {
              setError(ce.tooManyAttempts || "Too many failed attempts. Please check your card details or try a different card.");
            } else {
              const messages = Array.isArray(errors)
                ? errors.flatMap((e) => {
                    const label = fieldLabels[e?.fieldName] || "";
                    if (Array.isArray(e?.messages)) return e.messages.map((m) => ({ label, message: m }));
                    if (typeof e?.message === "string") return [{ label, message: e.message }];
                    if (typeof e === "string") return [{ label: "", message: e }];
                    return [{ label: "", message: ce.cardVerifyFailed || "Card verification failed." }];
                  })
                : [{ label: "", message: ce.cardVerifyFailed || "Card verification failed. Please check your details and try again." }];
              setError(messages);
            }
            return next;
          });
        }
      } else {
        const token = data.singleUseToken.singleUseTokenId;
        setSurchargePercentage(data.singleUseToken.creditCard.surchargePercentage || 0);
        sessionStorage.setItem("cardDetails", JSON.stringify(data.singleUseToken.creditCard));
        try {
          const result      = await submitOrder(token);
          const orderNumber = result?.takePaymentAndPlaceOrder?.OrderNumber;
          sessionStorage.setItem("orderNumber", orderNumber || "");
          sessionStorage.removeItem("plateLockExpiry");
          trackOrderPlaced(orderNumber);
          navigate("/create/success");
        } catch (err) {
          const errMsg = Array.isArray(err) ? err[0]?.message : (err?.message || "Please try again or contact support.");
          let msg = ce.paymentFailedPrefix || "Payment processing failed. ";
          if (errMsg.includes("declined"))      msg += ce.cardDeclined    || "Your card was declined. Please check your card details or try a different card.";
          else if (errMsg.includes("timeout"))  msg += ce.paymentTimeout  || "The payment request timed out. Please try again.";
          else if (errMsg.includes("network"))  msg += ce.paymentNetwork  || "Network error. Please check your connection and try again.";
          else msg += errMsg;
          setError(msg);
          setPaymentProcessing(false);
        }
      }
    });
  };

  const handlePlaceOrderVoucherOnly = async () => {
    trackPlaceOrderClick();
    setPaymentProcessing(true);
    setError(null);
    try {
      const result      = await submitOrder(null);
      const orderNumber = result?.takePaymentAndPlaceOrder?.OrderNumber;
      sessionStorage.setItem("orderNumber", orderNumber || "");
      sessionStorage.removeItem("plateLockExpiry");
      trackOrderPlaced(orderNumber);
      navigate("/create/success");
    } catch (err) {
      setError(ce.orderFailed || "Failed to place order. Please try again.");
      setPaymentProcessing(false);
    }
  };

  const submitOrder = async (token) => {
    setIsSubmittingOrder(true);
    try {
      const createOrderInput = {
        OrderNumberType : ORDER_NUMER_TYPE,
        SingleUseToken  : token,
        OrderNumberFormat: ORDER_NUMER_FORMAT,
        OrderType       : NEW_ORDER_TYPE,
        PlateCombo      : combination,
        ClientId        : orderData.personalDetails.licenceNumber,
        ChannelId       : VPLATES_CHANNEL_ID,
        FirstName       : orderData.personalDetails.firstName,
        LastName        : orderData.personalDetails.lastName,
        Email           : orderData.personalDetails.email,
        Mobile          : orderData.personalDetails.phone,
        OfficeId        : "0960",
        VRISId          : 0,
        CPMDemandId     : "",
        TierProductId   : "Perpetual",
        VariantId       : orderData.variantId,
        VehicleTypeId   : orderData.vehicleType == "car" ? 1 : 2,
        StylePrice      : basePrice,
        IncludesPlates  : true,
        GiftVoucherCode : appliedVoucher?.code || '',
        CreatedBy       : orderData.personalDetails.firstName,
        ShippingAddress : orderData.personalDetails.address
      };
      return await executeMutation(createOrder, { input: createOrderInput });
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const getPlateImages = () => {
    if (!orderData) return [];
    if (orderData.plateImages?.isOOE) {
      const images = [];
      if (orderData.plateImages.standard) images.push(orderData.plateImages.standard);
      if (orderData.plateImages.slimline) images.push(orderData.plateImages.slimline);
      return images;
    }
    const single = orderData.plateImages?.single || orderData.plateImages?.standard || orderData.plateImages?.slimline || "";
    if (!single) return [];
    return orderData.vehicleType === 'motorcycle' ? [single] : [single, single];
  };

  if (loading || !orderData) {
    return (
      <div id="content">
        <div className="row">
          <div className="page-header page-header--small">
            <div className="page-header__content">
              <h1 className="page-header__title">{c.pageTitle || 'Create a plate'}</h1>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="plate-wizard">
            <div className="loading__panel" role="alert">{c.loadingText || 'Loading...'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="content">
      <div className="row">
        <div className="page-header">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Create a plate'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <Wizard
            steps={wizardSteps}
            currentStep={3}
            backLink="/create/personal-details"
            isDisabled={true}
          />

          <div className="plate-wizard__form plate-wizard__form--payment">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">{c.sectionTitle || 'Checkout'}</h2>
            </div>

            <div>
              <div className="order-payment">
                <p className="order-payment__accessible-loading visuallyhidden" role="alert"></p>

                {timerExpired && (
                  <div className="error-bar error-bar--timer" role="alert" tabIndex="-1">
                    <p className="error-bar__message">
                      {c.timerExpiredMessage || 'Sorry, your session has expired. Please '}
                      <a href="/create/check-combination">{c.timerExpiredLinkText || 'check your combination'}</a>
                      {c.timerExpiredMessageSuffix || ' again.'}
                    </p>
                  </div>
                )}

                {!timerExpired && timerOutput && (
                  <div className="order-payment__timer">
                    <p className="order-payment__timer-text">
                      {c.timerLabel || 'Item reserved for'} <strong>{timerOutput}</strong>
                    </p>
                  </div>
                )}

                {!timerExpired && error && !Array.isArray(error) && (
                  <div className="error-bar error-bar--component-prep-error" role="alert" tabIndex="-1">
                    {(Array.isArray(error) ? error : [error]).map((msg, i) => (
                      <p key={i}>{typeof msg === "object" && msg.label ? <><strong>{msg.label}:</strong> {msg.message}</> : (msg?.message || msg)}</p>
                    ))}
                  </div>
                )}

                {getPlateImages().length > 0 && (
                  <div className="order-payment__order-value--images">
                    {getPlateImages().map((src, i) => (
                      <img key={i} className="order-payment__order-image" src={src} alt={c.plateImageAlt || "Preview image of your plate"} />
                    ))}
                  </div>
                )}

                {timerExpired && (
                  <div className="order-payment__inner">
                    <div className="order-payment__form-actions">
                      <a href="/create/check-combination" className="button button--primary">
                        <span className="button__text">{c.timerRestartText || 'Start again'}</span>
                      </a>
                    </div>
                  </div>
                )}

                {!timerExpired && <div className="order-payment__inner">
                  <div className="order-payment__order">
                    <h3 className="visuallyhidden">{c.orderSummaryTitle || 'Your order'}</h3>
                    <dl className="order-payment__order-list">
                      <div className="order-payment__order-item">
                        <dt className="order-payment__order-key">{c.vehicleTypeLabel || 'Vehicle type'}</dt>
                        <dd className="order-payment__order-value">{vehicleType === "car" ? "Car" : "Motorcycle"}</dd>
                      </div>
                      <div className="order-payment__order-item">
                        <dt className="order-payment__order-key">{c.combinationLabel || 'Combination'}</dt>
                        <dd className="order-payment__order-value">{orderData.combination}</dd>
                      </div>
                      <div className="order-payment__order-item">
                        <dt className="order-payment__order-key">{c.plateStyleLabel || 'Plate style'}</dt>
                        <dd className="order-payment__order-value">{orderData.productName}</dd>
                      </div>
                      {orderData.sizeName && (
                        <div className="order-payment__order-item">
                          <dt className="order-payment__order-key">{c.sizeLabel || 'Size'}</dt>
                          <dd className="order-payment__order-value">
                            {orderData.isOOE
                              ? <>{c.oneOfEachLabel || 'One of each'} ({orderData.sizeName}) </>
                              : orderData.sizeName}
                          </dd>
                        </div>
                      )}
                      {orderData.variantColour && (
                        <div className="order-payment__order-item">
                          <dt className="order-payment__order-key">{c.colourLabel || 'Colour'}</dt>
                          <dd className="order-payment__order-value">{orderData.variantColour}</dd>
                        </div>
                      )}
                      {orderData.personalDetails?.address && (
                        <>
                          <div className="order-payment__order-item order-payment__order-item--address">
                            <dt className="order-payment__order-key order-payment__order-key--inline">{c.shippingAddressLabel || 'Shipping address:'}</dt>
                            {' '}
                            <dd className="order-payment__order-value order-payment__order-value--inline">{orderData.personalDetails.address}</dd>
                          </div>
                        </>
                      )}
                    </dl>
                    <details className="order-payment__order-delivery">
                      <summary className="order-payment__order-delivery-title">{c.deliveryTitle || 'Estimated delivery time'}</summary>
                      <div className={`order-payment__order-delivery-content ${styles.deliveryContent}`}>
                        <div class="disclaimer">
                          {<span dangerouslySetInnerHTML={{ __html: c.deliveryDisclaimerHtml }} />}
                        </div>
                        {Array.isArray(c.deliveryTable) && c.deliveryTable.length > 0 && (
                          <table
                            className="order-payment__order-delivery-table"
                            style={{ margin: '20px auto 0', width: 'auto' }}
                          >
                            <thead>
                              <tr>
                                <th>{c.deliveryTableItemHeader || 'Item'}</th>
                                <th>{c.deliveryTableTimeHeader || 'Estimated delivery time'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.deliveryTable.map((row, i) => (
                                <tr key={i}>
                                  <td>{row.item}</td>
                                  <td>{row.time}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </details>
                  </div>
                  <form className="order-payment__form" onSubmit={(e) => e.preventDefault()}>

                    {/* Gift voucher section */}
                    {ENABLE_GIFT_CARDS && (
                      <div className="order-payment__entry">
                        <h3 className="order-payment__entry-title">{c.voucherSectionTitle || 'Have a gift voucher?'}</h3>
                        {!appliedVoucher ? (
                          <div className="form__input-group">
                            <div className={`input${voucherError ? " input--error" : ""}`}>
                              <div className="input__header">
                                <label className="input__label" htmlFor="voucher-code">{c.voucherCodeLabel || 'Gift voucher code'}</label>
                              </div>
                              <input
                                className="input__ctrl"
                                type="text"
                                id="voucher-code"
                                value={voucherCode}
                                onChange={(e) => { setVoucherCode(e.target.value); setVoucherError(""); }}
                                placeholder={c.voucherPlaceholder || "Enter your voucher code"}
                                maxLength={50}
                                autoComplete="off"
                                disabled={voucherChecking}
                              />
                              {voucherError && (
                                <div className="input__error-container">
                                  <div className="input__error"><p>{voucherError}</p></div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="button button--secondary"
                              onClick={handleApplyVoucher}
                              disabled={voucherChecking}
                            >
                              <span className="button__text">{voucherChecking ? (c.checkingVoucherText || "Checking...") : (c.applyVoucherText || "Apply voucher")}</span>
                            </button>
                          </div>
                        ) : (
                          <div className="order-payment__voucher-applied">
                            <dl className="order-payment__order-list">
                              <div className="order-payment__order-item">
                                <dt className="order-payment__order-key">{c.voucherCodeKey || 'Voucher code'}</dt>
                                <dd className="order-payment__order-value">{appliedVoucher.code}</dd>
                              </div>
                              <div className="order-payment__order-item">
                                <dt className="order-payment__order-key">{c.voucherBalanceKey || 'Voucher balance'}</dt>
                                <dd className="order-payment__order-value">${appliedVoucher.balance.toFixed(2)} {appliedVoucher.currency}</dd>
                              </div>
                              <div className="order-payment__order-item">
                                <dt className="order-payment__order-key">{c.discountAppliedKey || 'Discount applied'}</dt>
                                <dd className="order-payment__order-value">-${voucherDiscount.toFixed(2)} {appliedVoucher.currency}</dd>
                              </div>
                            </dl>
                            {voucherFullyCovered && (
                              <p style={{ marginTop: "8px", color: "#32831f", fontWeight: "700" }}>
                                {c.voucherFullyCoveredText || 'This voucher covers the full order amount. No card payment required.'}
                              </p>
                            )}
                            {!voucherFullyCovered && (
                              <p style={{ marginTop: "8px" }}>
                                {c.remainingBalanceText || 'Remaining balance to pay by card:'} <strong>${cardAmount.toFixed(2)} AUD</strong>
                              </p>
                            )}
                            <button
                              type="button"
                              className="button button--secondary"
                              onClick={handleRemoveVoucher}
                              style={{ marginTop: "12px" }}
                            >
                              <span className="button__text">{c.removeVoucherText || 'Remove voucher'}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Card payment section — hidden when voucher covers everything */}
                    {!voucherFullyCovered && (
                      <div className="order-payment__entry">
                        <h3 className="order-payment__entry-title">{c.paymentDetailsTitle || 'Payment details'}</h3>

                        {error && (
                          <div className="order-payment__form-errors" role="alert" tabIndex="-1">
                            <div className="order-payment__form-errors-inner">
                              <h4 className="visuallyhidden">There are errors in this form</h4>
                              <p className="order-payment__form-errors-intro">{c.formErrorsIntro || 'Please review the following areas before submitting:'}</p>
                              <ul className="order-payment__form-errors-list">
                                {(Array.isArray(error) ? error : [error]).map((msg, i) => (
                                  <li key={i} className="order-payment__form-errors-item">
                                    <span>{typeof msg === "object" && msg.label ? <><span className="order-payment__form-errors-label">{msg.label}:</span> {msg.message}</> : (msg?.message || msg)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        <div id="trusted-frame-container">
                          <div data-quickstream-api="creditCardContainer"></div>
                        </div>
                      </div>
                    )}

                    {/* Payment summary — always visible once order data is loaded */}
                    <div className="order-payment__summary">
                      <h3 className="order-payment__summary-title">{c.summaryTitle || 'Payment summary'}</h3>
                      <dl className="order-payment__summary-list">
                        <div className="order-payment__summary-list-item">
                          <dt className="order-payment__summary-list-key">{c.platePriceKey || 'Plate price'}</dt>
                          <dd className="order-payment__summary-list-value">${basePrice.toFixed(2)} AUD</dd>
                        </div>
                        {appliedVoucher && (
                          <div className="order-payment__summary-list-item">
                            <dt className="order-payment__summary-list-key">{c.voucherDiscountKey || 'Gift voucher discount'}</dt>
                            <dd className="order-payment__summary-list-value">-${voucherDiscount.toFixed(2)} {appliedVoucher.currency}</dd>
                          </div>
                        )}
                        {!voucherFullyCovered && appliedVoucher && (
                          <div className="order-payment__summary-list-item">
                            <dt className="order-payment__summary-list-key">{c.cardAmountKey || 'Amount charged to card'}</dt>
                            <dd className="order-payment__summary-list-value">${cardAmount.toFixed(2)} AUD</dd>
                          </div>
                        )}
                        {surchargePercentage > 0 && (
                          <div className="order-payment__summary-list-item">
                            <dt className="order-payment__summary-list-key">
                              {c.cardFeeKey || 'Card payment fee'}
                              {c.feeTooltipHtml && <Tooltip content={<span dangerouslySetInnerHTML={{ __html: c.feeTooltipHtml }} />} type="question" />}
                            </dt>
                            <dd className="order-payment__summary-list-value">${cardSurcharge.toFixed(2)}</dd>
                          </div>
                        )}
                        <div className="order-payment__summary-list-item">
                          <dt className="order-payment__summary-list-key">{c.deliveryKey || 'Delivery'}</dt>
                          <dd className="order-payment__summary-list-value">{c.deliveryFreeValue || 'FREE'}</dd>
                        </div>
                        <div className="order-payment__summary-list-item order-payment__summary-list-item--total">
                          <dt className="order-payment__summary-list-key">{c.totalDueKey || 'Total due'}</dt>
                          <dd className="order-payment__summary-list-value">
                            ${voucherFullyCovered ? "0.00" : (cardAmount + cardSurcharge).toFixed(2)} AUD
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <div className="order-payment__form-field">
                      <input
                        type="checkbox"
                        id="terms-checkbox"
                        className="order-payment__form-checkbox-input visuallyhidden visuallyhidden--no-focus"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <label className="order-payment__form-checkbox-label" htmlFor="terms-checkbox">
                        {c.termsLabelBefore || 'I agree to the'}{' '}
                        <a href={c.termsLabelLink || '/terms'} className="order-payment__form-checkbox-label-link">
                          {c.termsLabelLinkLabel || 'Terms and Conditions'}
                        </a>{' '}
                        {c.termsLabelAfter || 'of sale'}
                      </label>
                    </div>

                    <div className="order-payment__form-actions">
                      {voucherFullyCovered ? (
                        <button
                          type="button"
                          className={`order-payment__form-cta${(paymentProcessing || !termsAccepted) ? " order-payment__form-cta--disabled" : ""}`}
                          disabled={paymentProcessing || !termsAccepted}
                          onClick={handlePlaceOrderVoucherOnly}
                        >
                          {paymentProcessing ? (c.processingText || "Processing...") : (c.placeOrderText || "Place order")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`order-payment__form-cta${(paymentProcessing || !termsAccepted) ? " order-payment__form-cta--disabled" : ""}`}
                          disabled={paymentProcessing || !termsAccepted}
                          onClick={handlePlaceOrder}
                        >
                          {paymentProcessing ? (c.processingText || "Processing...") : (c.placeOrderText || "Place order")}
                        </button>
                      )}
                    </div>
                  </form>
                </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentPage;
